/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: ApplicationKeysPage.tsx
 ??? Created: 2025-01-05
 ?? Description:
    Main API Keys management page with data grid, CRUD operations,
    and comprehensive API key lifecycle management.

 ?? Notes:
    - Integrates DataGridSimple for API key listing and management
    - Supports API key creation, editing, deletion, and regeneration
    - Implements search and filtering functionality
    - Uses confirmation modals for destructive actions
    - Integrates with permissions system for access control

 ?? Last Updated: 2025-01-XX by Rodrigo Cuadra (Refactored)
----------------------------------------------------------------*/

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { Key } from 'lucide-react';
import { useNavigationStore } from '../../store/navigation';
import { useAuthStore } from '../../store/auth';
import { useNotifications } from '../../contexts/NotificationContext';
import { ModulePageContainer } from '../../components/layouts';
import { deleteApiKey } from './api';
import { navigateToApiKeyDuplicate } from './apiKeysActions';
import { useApiKeysList } from './hooks';
import { useApiKeysColumnRenderers, ApiKeysActions } from './components';
import { ApiKey } from './types';

export default function ApplicationKeysPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { setCurrentPageTitle } = useNavigationStore();
  const { user } = useAuthStore();
  const { showSuccess, showError } = useNotifications();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [apiKeyToDelete, setApiKeyToDelete] = useState<ApiKey | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => {
      setCurrentPageTitle(null);
    };
  }, [setCurrentPageTitle]);

  const tenantSlug = user?.tenant?.slug ?? t('apiKeys.defaultTenant');
  const userId = user?.id;

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useApiKeysList();

  const columnRenderers = useApiKeysColumnRenderers();

  const handleCreateApiKey = useCallback(() => {
    navigate('/admin/admin/app-keys/new');
  }, [navigate]);

  const handleEditApiKey = useCallback(
    (apiKey: ApiKey) => {
      navigate(`/admin/admin/app-keys/${apiKey.uuid ?? apiKey.id}`);
    },
    [navigate],
  );

  const handleDuplicateApiKey = useCallback(
    (apiKey: ApiKey) => {
      navigateToApiKeyDuplicate(navigate, apiKey.uuid ?? apiKey.id);
    },
    [navigate],
  );

  const handleDeleteApiKey = useCallback((apiKey: ApiKey) => {
    setApiKeyToDelete(apiKey);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!apiKeyToDelete) return;

    try {
      setIsDeleting(true);
      await deleteApiKey(apiKeyToDelete.id, tenantSlug, userId);
      showSuccess(t('apiKeys.messages.deleteSuccess'));
      setShowDeleteModal(false);
      setApiKeyToDelete(null);
      refresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : t('apiKeys.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [apiKeyToDelete, refresh, showSuccess, showError, tenantSlug, t, userId]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            titleIcon={<Key className="h-5 w-5 text-primary" />}
            data={data}
            columns={columns.map((col) => ({
              ...col,
              render: columnRenderers[col.key as keyof typeof columnRenderers],
            }))}
            loading={loading}
            pagination={pagination}
            searchQuery={searchQuery}
            onSearch={handleSearch}
            onSort={handleSort}
            onColumnToggle={handleColumnToggle}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onAdd={handleCreateApiKey}
            onEdit={handleEditApiKey}
            onDuplicate={handleDuplicateApiKey}
            onDelete={handleDeleteApiKey}
            renderActions={(item) => (
              <ApiKeysActions
                item={item as ApiKey}
                onEdit={handleEditApiKey}
                onDuplicate={handleDuplicateApiKey}
                onDelete={handleDeleteApiKey}
              />
            )}
            title={t('apiKeys.title')}
            addButtonText={t('common.add')}
            searchPlaceholder={t('apiKeys.search')}
            emptyMessage={t('apiKeys.noApiKeys')}
            deleteTitle={t('apiKeys.deleteTitle')}
            deleteMessage={(item: ApiKey) =>
              item?.name
                ? t('apiKeys.confirmDeleteMessage', { name: item.name })
                : t('apiKeys.confirmDeleteDefault')
            }
          />

          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            title={t('apiKeys.deleteTitle')}
            message={
              apiKeyToDelete
                ? t('apiKeys.confirmDeleteMessage', { name: apiKeyToDelete.name })
                : ''
            }
            confirmText={t('apiKeys.deleteText')}
            cancelText={t('apiKeys.cancelText')}
            isLoading={isDeleting}
            type="danger"
          />
        </>
      }
      formView={null}
    />
  );
}
